import React, { useState } from 'react';

import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Modal,
  FormHelperText
} from '@mui/material';
import { collection, doc, setDoc } from 'firebase/firestore';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { uploadFile } from '../../api/aws/s3';
import { negotiation as negotiationDoc } from '../../api/firebase/firestore/negotiation';
import {
  sendNoticeMail,
  sendNoticeMailFromClientAgent
} from '../../api/sendgrid';
import {
  black,
  blueButton,
  primaryButtonBackground,
  tertiaryButtonBackground
} from '../../constants/Colors';
import { sizeBelowTwentyMb } from '../../constants/Location';
import { chatType, screenType } from '../../constants/enum';
import { throwError } from '../../modules/error';

import type { NegotiationType } from '../../api/firebase/firestore/negotiation/negotiationType';
import type { User } from 'firebase/auth';

const styles = {
  blueButton,
  modal: {
    '@media screen and (max-width:480px)': {
      margin: '0 16px'
    },
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    padding: 2
  },
  paper: {
    '@media screen and (max-width:480px)': {
      padding: '24px 16px',
      width: '100%'
    },
    backgroundColor: 'white',
    padding: '24px 0',
    position: 'relative',
    width: '480px'
  },
  tertiaryButtonBackground
};

interface Props {
  open: boolean;
  handleClose: () => void;
  negotiation: NegotiationType | null;
  currentUser: User | null | undefined;
  screen: number;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const allowedExtensions = [
  'pdf',
  'ppt',
  'pptx',
  'potx',
  'pot',
  'odp',
  'ppsx',
  'pps',
  'pptm',
  'potm',
  'ppsm',
  'doc',
  'docx',
  'dotx',
  'dot',
  'docm',
  'dotm',
  'odt',
  'xls',
  'xlsx',
  'xltx',
  'xlt',
  'xlsm',
  'xlsb',
  'xltm',
  'prn',
  'dif',
  'slk',
  'xlam',
  'xla',
  'ods',
  'txt',
  'rtf',
  'csv',
  'tsv',
  'jpg',
  'jpeg',
  'png',
  'gif',
  'svg',
  'webp'
];

const FileUploadModal = (props: Props): React.ReactElement => {
  const { t } = useTranslation();
  const { handleClose, open, negotiation, currentUser, screen, setLoading } =
    props;
  const [files, setFiles] = useState<File[]>([]);

  const [errors, setErrors] = useState<
    Array<{ value: string; errorType: string }>
  >([]);

  function isAllowedExtension(filename: string): boolean {
    const extension = filename.split('.').pop()?.toLowerCase();
    return extension ? allowedExtensions.includes(extension) : false;
  }

  const onDrop = (acceptedFiles: File[]): void => {
    const newErrors: Array<{ value: string; errorType: string }> = [];

    acceptedFiles.forEach((file) => {
      if (sizeBelowTwentyMb(file.size)) {
        newErrors.push({ errorType: 'size', value: `${file.name}` });
      } else if (!isAllowedExtension(file.name)) {
        newErrors.push({ errorType: 'prefix', value: `${file.name}` });
      } else {
        setFiles((prevFiles) => [...prevFiles, file]);
      }
    });

    setErrors((prevErrors) => [...prevErrors, ...newErrors]);
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const handleDelete = (index: number): void => {
    setFiles((prevFiles) => prevFiles.filter((file, i) => i !== index));
  };

  const handleDeleteError = (index: number): void => {
    setErrors((prevErrors) =>
      prevErrors.filter((error, i) => error && i !== index)
    );
  };

  const handleFileUpload = async (): Promise<void> => {
    setLoading(true);
    const promises = files.map(async (file) => {
      const filePath = `negotiations/${
        negotiation?.negotiationId ?? ''
      }/file/${Date.now()}_${file.name}`;
      try {
        const fileList: FileList = arrayToFileList(file);
        uploadFile(fileList, filePath);
        return await saveFileNameAndPath(file.name, filePath);
      } catch (error) {
        throwError(error);
      }
    });

    await Promise.all(promises);

    const fileArray = files.map((file) => file.name);

    if (screen === screenType.publisher) {
      if (negotiation?.agentId) {
        sendNoticeMail(
          negotiation?.agentMainUid ?? '',
          negotiation?.agentMember ?? [],
          fileArray.join(', '),
          negotiation,
          '添付ファイル',
          currentUser?.uid ?? ''
        );
      } else if (negotiation?.clientId) {
        sendNoticeMail(
          negotiation?.clientMainUid ?? '',
          negotiation?.clientMember ?? [],
          fileArray.join(', '),
          negotiation,
          '添付ファイル',
          currentUser?.uid ?? ''
        );
      }
    } else if (screen === screenType.agent || screen === screenType.client) {
      sendNoticeMailFromClientAgent(
        negotiation?.publisherMainUid ?? '',
        negotiation?.publisherMember ?? [],
        fileArray.join(', '),
        negotiation as NegotiationType,
        '添付ファイル',
        currentUser?.uid ?? ''
      );
    }
    setLoading(false);
    setFiles([]);
    handleClose();
  };

  const saveFileNameAndPath = async (
    fileName: string,
    filePath: string
  ): Promise<void> => {
    const negotiationRef = negotiationDoc(negotiation?.negotiationId ?? '');
    const chatRef = doc(collection(negotiationRef, 'chats'));

    await setDoc(chatRef, {
      created: new Date(),
      fileId: filePath,
      fileName,
      senderId: currentUser?.uid,
      type: chatType.file
    });
  };

  function arrayToFileList(file: File): FileList {
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    return dataTransfer.files;
  }

  const handleClick = async (): Promise<void> => {
    await handleFileUpload();
  };

  return (
    <>
      <Modal open={open} onClose={handleClose} sx={styles.modal}>
        <Typography sx={styles.paper}>
          <Typography
            sx={{
              '@media screen and (max-width:480px)': {
                p: 0
              },
              p: '0 40px'
            }}
          >
            <IconButton
              edge="end"
              color="inherit"
              aria-label="close"
              onClick={handleClose}
              style={{
                color: '#757575',
                position: 'absolute',
                right: '40px',
                top: '24px'
              }}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" gutterBottom sx={{ color: black }}>
              {t('message.fileUpload')}
            </Typography>
            <Box
              sx={{
                alignItems: 'center',
                backgroundColor: '#F2F2F2',
                border: '1px solid #DBDBE0',
                display: 'flex',
                height: '148px',
                justifyContent: 'center',
                mt: '24px'
              }}
              {...getRootProps()}
            >
              <input {...getInputProps()} />
              <label>
                <Typography sx={{ textAlign: 'center' }}>
                  <Typography sx={{ color: 'black', fontSize: '14px' }}>
                    {t('message.dragAndDrop')}
                  </Typography>
                  <Typography
                    sx={{ color: '#757575', fontSize: '12px', mt: '12px' }}
                  >
                    {t('message.or')}
                  </Typography>
                  <Button
                    component="span"
                    variant="contained"
                    sx={[
                      styles.blueButton,
                      {
                        mt: '12px'
                      }
                    ]}
                  >
                    {t('message.selectFromFile')}
                  </Button>
                </Typography>
              </label>
            </Box>
            <Typography
              sx={{
                alignItems: 'flex-end',
                display: 'flex',
                mb: '8px',
                mt: '16px'
              }}
            >
              <Typography sx={{ color: 'black', fontSize: '14px' }}>
                {t('message.uploadFile')}
              </Typography>
              <Typography
                sx={{ color: '#757575', fontSize: '12px', ml: '8px' }}
              >
                {t('message.maxFileSize')}
              </Typography>
            </Typography>
            {files.map((file, index) => (
              <Typography
                sx={{
                  alignItems: 'center',
                  borderBottom: '1px solid #DBDBE0',
                  borderTop: index === 0 ? '1px solid #DBDBE0' : undefined,
                  display: 'flex'
                }}
                key={index}
              >
                <DescriptionOutlinedIcon
                  sx={{ color: black, marginRight: '8px' }}
                />
                <Typography
                  sx={{
                    color: black,
                    mb: '12px',
                    mt: '12px',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    wordWrap: 'break-word'
                  }}
                >
                  {file.name}
                </Typography>
                <Typography sx={{ flexGrow: 1, textAlign: 'right' }}>
                  <IconButton
                    sx={{ color: '#757575', marginLeft: '8px' }}
                    onClick={() => handleDelete(index)}
                  >
                    <CancelIcon />
                  </IconButton>
                </Typography>
              </Typography>
            ))}
            {errors.map((error, index) => (
              <Typography
                sx={{
                  alignItems: 'center',
                  borderBottom: '1px solid #DBDBE0',
                  borderTop: index === 0 ? '1px solid #DBDBE0' : undefined,
                  display: 'flex'
                }}
                key={index}
              >
                <DescriptionOutlinedIcon
                  sx={{ color: black, marginRight: '8px' }}
                />
                <Typography
                  sx={{
                    color: black,
                    display: 'block',
                    mb: '12px',
                    mt: '12px',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    wordWrap: 'break-word'
                  }}
                >
                  <Typography>{error.value}</Typography>
                  <FormHelperText error>
                    {error.errorType === 'prefix'
                      ? t('message.fileTypeNotSupported')
                      : t('message.fileTooLarge')}
                  </FormHelperText>
                </Typography>
                <Typography sx={{ flexGrow: 1, textAlign: 'right' }}>
                  <IconButton
                    sx={{ color: '#757575', marginLeft: '8px' }}
                    onClick={() => handleDeleteError(index)}
                  >
                    <CancelIcon />
                  </IconButton>
                </Typography>
              </Typography>
            ))}
            <Box display="flex" justifyContent="center" marginTop={'80px'}>
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={[
                  styles.tertiaryButtonBackground,
                  {
                    color: '#757575',
                    marginRight: '8px',
                    minHeight: '42px',
                    width: '192px'
                  }
                ]}
              >
                {t('message.cancel')}
              </Button>
              <Button
                sx={[
                  primaryButtonBackground,
                  { minHeight: '42px', width: '192px' }
                ]}
                variant="contained"
                disabled={files.length === 0 || errors.length > 0}
                onClick={handleClick}
              >
                {t('message.sendMessage')}
              </Button>
            </Box>
          </Typography>
        </Typography>
      </Modal>
    </>
  );
};

export default FileUploadModal;
